import React, { useState } from 'react';
import classnames from 'classnames/bind';

import { Category } from '@services';
import { breakpoints, Icon, Typography, useMediaQuery } from 'gd-design-library';

import styles from './MobileMenu.module.scss';
import { Button } from '../baseComponents/Button';
import { HeaderActions } from '../HeaderActions';
import { TenantSelector } from '../TenantSelector';

const CN = 'mobile-menu';
const headerClassName = `${CN}--header`;
const headerTextClassName = `${CN}--header-text`;
const listClassName = `${CN}--list`;
const footerClassName = `${CN}--footer`;
const cx = classnames.bind(styles);

interface MobileMenuProps {
  categories: Category[];
  onCategoryClick: (categoryId: string) => Promise<boolean>;
  onClose: () => void;
  isDrawerOpen: boolean;
  handleCartDrawerToggle: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  categories,
  onCategoryClick,
  onClose,
  isDrawerOpen,
  handleCartDrawerToggle
}) => {
  const [ categoriesHistory, setCategoriesHistory ] = useState<Category[]>([]);
  const isLargeScreen = useMediaQuery(`(min-width: ${breakpoints.xl})`);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);

  const handleCategoryArrowClick = (category: Category): void => (
    setCategoriesHistory(prevCategoriesHistory => (
      [ ...prevCategoriesHistory, category ]
    ))
  );

  const handleBackArrowClick = (): void => (
    setCategoriesHistory(prevCategoriesHistory => (
      prevCategoriesHistory.slice(0, -1)
    ))
  );

  const handleCategoryClick = (categoryId?: string): void => {
    onCategoryClick(categoryId ?? '')
      .then(onClose);
  };

  const categoriesToShow = categoriesHistory.length ?
    categoriesHistory[categoriesHistory.length - 1].children ?? [] :
    categories;

  const headerText = categoriesHistory.length ?
    categoriesHistory[categoriesHistory.length - 1].name :
    'Categories';

  return (
    <>
      <div
        className={ cx('backdrop', { open: isDrawerOpen }) }
        onClick={ onClose }
      />
      <div
        className={ cx(CN, { open: isDrawerOpen }) }
        role='dialog'
        aria-modal='true'
        aria-label='mobile-menu'
      >
        <div>
          <div className={ cx(headerClassName) }>
            {!!categoriesHistory.length && (
              <Button
                isIcon
                variant='inherit'
                onClick={ (): void => handleBackArrowClick() }
                aria-label='Go back'
              >
                <Icon name='arrowLeft' />
              </Button>
            )}
            <Typography color='inherit' variant='h6' className={ cx(headerTextClassName) }>
              {headerText}
            </Typography>
          </div>
          <ul className={ cx(listClassName) }>
            {!categoriesHistory.length && (
              <li>
                <Button variant='inherit' onClick={ (): void => handleCategoryClick() }>
                All
                </Button>
              </li>
            )}
            {
              categoriesToShow.map(category => (
                <li key={ category.id }>
                  <Button variant='inherit' onClick={ (): void => handleCategoryClick(category.key) }>
                    {category.name}
                  </Button>
                  {category.children?.length ? (
                    <Button
                      isIcon
                      variant='inherit'
                      onClick={ (): void => handleCategoryArrowClick(category) }
                      aria-label={ `View ${category.name} subcategories` }
                    >
                      <Icon name='arrowRight' />
                    </Button>
                  ) : null}
                </li>
              ))
            }
          </ul>
        </div>
        <div className={ cx(footerClassName) }>
          {!isLargeScreen && (
            <TenantSelector />
          )}
          {!isDesktop && (
            <HeaderActions
              onCartClick={ handleCartDrawerToggle }
              onClose={ onClose }
            />
          )}
        </div>
      </div>
    </>
  );
};

